import { type ReactNode } from 'react';
import {
  default as ReactSelect,
  components,
  type ControlProps,
  type DropdownIndicatorProps,
  type Props as ReactSelectProps,
} from 'react-select';

import { blue } from '@/lib/colours';

import { ArrowDropDownIcon } from '@/components/icons';

export {
  components,
  type OnChangeValue,
  type SingleValueProps,
  type SingleValue,
} from 'react-select';

export interface DropdownBaseProps<
  Option = unknown,
  IsMulti extends boolean = false,
> extends ReactSelectProps<Option, IsMulti> {
  label: ReactNode;
  options: ReactSelectProps<Option>['options'];
}

export function DropdownBase<
  Option = unknown,
  IsMulti extends boolean = false,
>({ label, ...props }: DropdownBaseProps<Option, IsMulti>) {
  const Control = ({ children, ...props }: ControlProps<Option>) => {
    return (
      <components.Control {...props}>
        {label}
        {children}
      </components.Control>
    );
  };

  const DropdownIndicator = (props: DropdownIndicatorProps<Option>) => {
    return (
      <components.DropdownIndicator {...props}>
        <ArrowDropDownIcon />
      </components.DropdownIndicator>
    );
  };

  return (
    <ReactSelect
      {...props}
      menuPortalTarget={document.body}
      components={{
        Control,
        DropdownIndicator,
        IndicatorSeparator: null,
        ...props.components,
      }}
      styles={{
        container: (base) => {
          return {
            ...base,
            minWidth: '180px',
          };
        },
        control: (base, controlProps) => {
          return {
            ...base,
            flexWrap: 'unset',
            whiteSpace: 'nowrap',
            borderRadius: '28px',
            color: blue[600],
            borderColor: blue[600],
            ':hover': {
              borderColor: blue[800],
            },
            paddingLeft: '16px',
            paddingTop: '2px',
            paddingBottom: '2px',
          };
        },
        dropdownIndicator: (base) => {
          return {
            ...base,
            paddingLeft: '0',
            color: blue[600],
            ':hover': {
              borderColor: blue[800],
            },
          };
        },
        placeholder: (base) => {
          return {
            ...base,
            color: blue[600],
            ':hover': {
              borderColor: blue[800],
            },
          };
        },
        singleValue: (base) => {
          return {
            ...base,
            color: blue[600],
            ':hover': {
              borderColor: blue[800],
            },
          };
        },
        option: (base, state) => {
          return {
            ...base,
            whiteSpace: 'nowrap',
          };
        },
        menu: (base) => {
          return {
            ...base,
            width: 'auto',
          };
        },
        ...props.styles,
      }}
    />
  );
}
